import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

interface AuthModalProps {
  onClose: () => void;
  initialMode?: 'login';
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, initialMode = 'login' }) => {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'reset'>(initialMode);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      onClose();
    } else {
      setLocalError('Invalid email or password');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');
    try {
      const { apiService } = await import('../services/apiService');
      await apiService.authResetPassword(email);
      setResetSent(true);
    } catch {
      setLocalError('Failed to send reset email');
    }
    setLoading(false);
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <CloseBtn onClick={onClose}>&times;</CloseBtn>

        {mode === 'login' && (
          <>
            <Title>Sign In</Title>
            <Subtitle>Access your saved circuits, projects, and custom components</Subtitle>
            <Form onSubmit={handleLogin}>
              <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
              <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
              {localError && <ErrorMsg>{localError}</ErrorMsg>}
              <SubmitBtn type="submit" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</SubmitBtn>
            </Form>
            <Links>
              <LinkBtn onClick={() => { setMode('reset'); setLocalError(''); }}>Forgot password?</LinkBtn>
            </Links>
          </>
        )}

        {mode === 'reset' && (
          <>
            <Title>Reset Password</Title>
            {resetSent ? (
              <Subtitle>If the email exists, a reset link has been sent. Check your inbox.</Subtitle>
            ) : (
              <>
                <Subtitle>Enter your email to receive a password reset link</Subtitle>
                <Form onSubmit={handleResetPassword}>
                  <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                  {localError && <ErrorMsg>{localError}</ErrorMsg>}
                  <SubmitBtn type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</SubmitBtn>
                </Form>
              </>
            )}
            <Links>
              <LinkBtn onClick={() => { setMode('login'); setLocalError(''); setResetSent(false); }}>Back to sign in</LinkBtn>
            </Links>
          </>
        )}
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 420px;
  max-width: 90vw;
  position: relative;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px; right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  &:hover { color: #333; }
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 22px;
  color: #202D61;
`;

const Subtitle = styled.p`
  margin: 0 0 20px 0;
  font-size: 13px;
  color: #666;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #1a73e8; box-shadow: 0 0 0 2px rgba(26,115,232,0.15); }
`;

const SubmitBtn = styled.button`
  padding: 10px;
  background: #202D61;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 4px;
  &:hover { background: #2a3a7a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  color: #d93025;
  font-size: 13px;
  padding: 6px 0;
`;

const Links = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: #1a73e8;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;
